import React from 'react';
import { SparklesIcon } from 'lucide-react';
import { Switch } from '../ui/Switch';
import { FloatingDevice, LandingBackground } from '../../types';
import { BACKGROUND_TYPES, backgroundPreset, withBackgroundDefaults } from '../../data/background';

/**
 * Panel «Fondo» del constructor.
 *
 * Guarda su configuración en `pageConfig.background`, dentro de la propia
 * landing, así que el constructor, la vista previa y la página publicada leen
 * exactamente el mismo objeto. El valor por defecto es «Normal», es decir sin
 * efecto: ninguna landing cambia de aspecto hasta que se elige otro.
 */

interface BackgroundSettingsProps {
  value?: LandingBackground;
  onChange: (config: LandingBackground) => void;
}
const DEVICES: {
  value: FloatingDevice;
  label: string;
}[] = [{
  value: 'desktop',
  label: 'Escritorio'
}, {
  value: 'tablet',
  label: 'Tablet'
}, {
  value: 'mobile',
  label: 'Móvil'
}];
export function BackgroundSettings({
  value,
  onChange
}: BackgroundSettingsProps) {
  const config = withBackgroundDefaults(value);
  const active = config.type !== 'normal';
  function set(partial: Partial<LandingBackground>): void {
    onChange({
      ...config,
      ...partial
    });
  }
  const devices = config.devices ?? ['desktop', 'tablet', 'mobile'];
  function toggleDevice(device: FloatingDevice): void {
    set({
      devices: devices.includes(device) ? devices.filter(d => d !== device) : [...devices, device]
    });
  }
  return <div className="mt-5 space-y-4 rounded-2xl border border-border bg-background p-5" data-id="element-315" data-mp="2xj12icj6m044">
      <h3 className="flex items-center gap-2 text-sm font-semibold text-foreground" data-id="element-316" data-mp="31kcsij5ywncu">
        <SparklesIcon className="h-4 w-4 text-primary" aria-hidden="true" data-id="element-317" data-mp="2nmfwbnvfeiyp" />
        Fondo
      </h3>
      <p className="text-xs text-muted-foreground" data-id="element-318" data-mp="2sjxvwnnjzlu1">
        Efectos muy sutiles, hechos solo con CSS. «Normal / Actual» es el valor
        por defecto y deja la landing exactamente como está ahora.
      </p>

      <fieldset data-id="element-319" data-mp="1h4j0hoazt211">
        <legend className="mb-2 text-xs font-medium text-muted-foreground" data-id="element-320" data-mp="x4hnpb7axauc">
          Tipo de fondo
        </legend>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2" data-id="element-321" data-mp="t2dzdlzjabrn">
          {/* Al elegir un efecto se aplican sus valores iniciales (los
              Chispas nacen ya visibles); después cada control manda. */}
          {BACKGROUND_TYPES.map(option => <button key={option.value} type="button" aria-pressed={config.type === option.value} onClick={() => set({
          type: option.value,
          ...backgroundPreset(option.value)
        })} className={`rounded-xl border p-3 text-left transition-colors ${config.type === option.value ? 'border-primary bg-primary/5' : 'border-border hover:bg-surface'}`} data-id="element-322" data-mp="jckz3h4udj2p">
              <span className="block text-sm font-medium text-foreground" data-id="element-323" data-mp="f0beddtcqjo">
                {option.label}
              </span>
              <span className="mt-0.5 block text-[11px] leading-snug text-muted-foreground" data-id="element-324" data-mp="1zqucxhbgjpfa">
                {option.hint}
              </span>
            </button>)}
        </div>
      </fieldset>

      {active && <div className="space-y-4 rounded-xl border border-border p-4" data-id="element-325" data-mp="22b9347i33xjp">
          <label className="block" data-id="element-326" data-mp="279tv5jn6nax2">
            <span className="mb-1.5 block text-xs font-medium text-muted-foreground" data-id="element-327" data-mp="278sijaqiqgd8">
              Intensidad
            </span>
            <div className="flex gap-2" data-id="element-328" data-mp="2h0f3hysnrtt6">
              {(['baja', 'media'] as const).map(level => <button key={level} type="button" aria-pressed={config.intensity === level} onClick={() => set({
            intensity: level
          })} className={`flex-1 rounded-lg border px-3 py-2 text-xs font-medium capitalize transition-colors ${config.intensity === level ? 'border-primary bg-primary/10 text-primary' : 'border-border text-muted-foreground hover:bg-surface'}`} data-id="element-329" data-mp="2h2htiitt9mxw">
                  {level}
                </button>)}
            </div>
          </label>

          <label className="block" data-id="element-330" data-mp="34pzn8jwmyv3i">
            <span className="mb-1.5 block text-xs font-medium text-muted-foreground" data-id="element-331" data-mp="38rkvfry86l7d">
              Densidad
            </span>
            <div className="flex gap-2" data-id="element-332" data-mp="37bqkyezqq0gv">
              {(['baja', 'media', 'alta'] as const).map(level => <button key={level} type="button" aria-pressed={(config.density ?? 'baja') === level} onClick={() => set({
            density: level
          })} className={`flex-1 rounded-lg border px-3 py-2 text-xs font-medium capitalize transition-colors ${(config.density ?? 'baja') === level ? 'border-primary bg-primary/10 text-primary' : 'border-border text-muted-foreground hover:bg-surface'}`} data-id="element-333" data-mp="32ce0u20977hz">
                  {level}
                </button>)}
            </div>
          </label>

          <label className="block" data-id="element-334" data-mp="3ui49ygr16nq">
            <span className="mb-1.5 block text-xs font-medium text-muted-foreground" data-id="element-335" data-mp="3tfgw0imxmck">
              Velocidad
            </span>
            <div className="flex gap-2" data-id="element-336" data-mp="3v2m9n38wgfjw">
              {([['muy_lenta', 'Muy lenta'], ['lenta', 'Lenta']] as const).map(([key, label]) => <button key={key} type="button" aria-pressed={config.speed === key} onClick={() => set({
            speed: key
          })} className={`flex-1 rounded-lg border px-3 py-2 text-xs font-medium transition-colors ${config.speed === key ? 'border-primary bg-primary/10 text-primary' : 'border-border text-muted-foreground hover:bg-surface'}`} data-id="element-337" data-mp="8tlcfrs0zszx">
                  {label}
                </button>)}
            </div>
          </label>

          <label className="block" data-id="element-338" data-mp="ne7amdfocqma">
            <span className="mb-1.5 flex items-center justify-between text-xs font-medium text-muted-foreground" data-id="element-339" data-mp="8quksciu3yq3">
              <span data-id="element-340" data-mp="dhb1q6lv62h7">Opacidad</span>
              <span className="tabular-nums text-foreground" data-id="element-341" data-mp="diclczgy3xaj">
                {config.opacity ?? 6}%
              </span>
            </span>
            <input type="range" min={2} max={20} step={1} value={config.opacity ?? 6} onChange={e => set({
          opacity: Number(e.target.value)
        })} className="w-full accent-primary" aria-label="Opacidad del fondo" data-id="element-342" data-mp="3vtqzl09bbmvj" />
          </label>

          <Switch id="bg-motion" label="Movimiento" checked={config.motion !== false} onChange={checked => set({
        motion: checked
      })} data-id="element-343" data-mp="3qvfqirqudged" />

          <div data-id="element-344" data-mp="1wi3js7qpywc7">
            <span className="mb-2 block text-xs font-medium text-muted-foreground" data-id="element-345" data-mp="1rpsip1k8t4i2">
              Mostrar en
            </span>
            <div className="flex flex-wrap gap-2" data-id="element-346" data-mp="1np09277gkxl9">
              {DEVICES.map(device => <button key={device.value} type="button" aria-pressed={devices.includes(device.value)} onClick={() => toggleDevice(device.value)} className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${devices.includes(device.value) ? 'border-primary bg-primary/10 text-primary' : 'border-border text-muted-foreground hover:bg-surface'}`} data-id="element-347" data-mp="1ip40duntghzd">
                  {device.label}
                </button>)}
            </div>
          </div>
        </div>}
    </div>;
}